import { useEffect, useState } from 'react';

interface UseLocalStorageImageProps {
  url: string;
  alt?: string;
  className?: string;
}

export const useLocalStorageImage = ({ url, alt, className }: UseLocalStorageImageProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchAndStoreImage = async () => {
      try {
        // Check if image exists in localStorage
        const cachedImage = localStorage.getItem(url);
        
        if (cachedImage) {
          setImageSrc(cachedImage);
          return;
        }

        // Fetch the image (include credentials for Google Workspace profile pics)
        const response = await fetch(url, {
          credentials: 'include',
          referrerPolicy: 'no-referrer',
        });
        if (!response.ok) {
          setImageSrc(url);
          return;
        }
        const blob = await response.blob();

        // Convert blob to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          
          // Store in localStorage
          try {
            localStorage.setItem(url, base64String);
            setImageSrc(base64String);
          } catch (e) {
            // Handle localStorage quota exceeded
            localStorage.clear();
            localStorage.setItem(url, base64String);
            setImageSrc(base64String);
          }
        };
        reader.readAsDataURL(blob);

      } catch (error) {
        // Fallback to original URL if fetch/caching fails
        setImageSrc(url);
      }
    };

    if (url) {
      fetchAndStoreImage();
    }
  }, [url]);

  const handleImageError = () => {
    setImageError(true);
  };

  // If image failed to load and we have alt text, show first letter
  if (imageError && alt) {
    const firstLetter = alt.charAt(0).toUpperCase();
    return (
      <div className={`${className} flex items-center justify-center bg-primary-1 text-primary-5 font-bold text-xl sm:text-2xl`}>
        {firstLetter}
      </div>
    );
  }

  // If no image source and no error yet, show nothing
  if (!imageSrc) {
    return null;
  }

  return <img src={imageSrc} className={className} alt={alt} onError={handleImageError} />;
};